// The front door, in a browser.
//
// Two choices and nothing else: sign in with Discord, or play as a guest. It
// is deliberately the first thing that happens -- before WebGPU, before the
// card library, before 137 MB of artwork -- because a visitor who is going to
// bounce should not cost a download, and because a signed-in player's decks
// are wanted by the screen that comes *after* the load.
//
// Inside a Discord Activity this never runs: Discord has already said who is
// playing, and asking again would be asking a question that is answered.

const el = <T extends HTMLElement>(id: string): T => {
  const node = document.getElementById(id);
  if (!node) throw new Error(`missing #${id}`);
  return node as T;
};

export type SignInChoice = 'discord' | 'guest';

export interface SignInOptions {
  /** Shown above the buttons when a previous attempt failed. */
  error?: string | undefined;
  /** Set when a saved sign-in was found to be stale, to say why we are asking. */
  expired?: boolean;
}

/**
 * Shows the screen and resolves with what was pressed.
 *
 * Both listeners are removed on the way out. The screen can be shown twice in
 * one page load -- a saved token that turns out to be stale drops the player
 * back here -- and a second set of listeners on the same buttons would resolve
 * a promise nobody is waiting on any more.
 */
export function askToSignIn(opts: SignInOptions = {}): Promise<SignInChoice> {
  const root = el('signin');
  const discord = el<HTMLButtonElement>('signin-discord');
  const guest = el<HTMLButtonElement>('signin-guest');
  const note = el('signin-note');

  const message = opts.error
    ? `Could not sign in: ${opts.error}`
    : opts.expired ? 'Your last sign-in has expired.' : '';
  note.textContent = message;
  note.hidden = message === '';

  root.hidden = false;

  return new Promise<SignInChoice>((resolve) => {
    const finish = (choice: SignInChoice) => () => {
      discord.removeEventListener('click', onDiscord);
      guest.removeEventListener('click', onGuest);
      // The Discord button navigates away, so the screen stays up behind the
      // redirect rather than flashing empty while the browser leaves.
      if (choice === 'guest') root.hidden = true;
      else discord.disabled = true;
      resolve(choice);
    };
    const onDiscord = finish('discord');
    const onGuest = finish('guest');
    discord.addEventListener('click', onDiscord);
    guest.addEventListener('click', onGuest);
  });
}

/** Takes the screen down, whoever put it up. */
export function hideSignIn(): void {
  const root = document.getElementById('signin');
  if (root) root.hidden = true;
}
